import React, { useState } from "react";
import styled from "@emotion/styled";
import { useForm } from "react-final-form";
import Button from "~/components/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/dialog";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";
import FormBottomRow from "~/components/form-bottom-row";
import { CustomerPicker } from "~/visuals/organisms/CustomerPickerFormField";
import { useSnackBar } from "~/components/snackbar";
import BinaryPickerFormField from "~/visuals/organisms/BinaryPickerFormField";
import { ValueRenderer } from "~/components/forms/ValueContext";
import TextFormField from "~/components/text-form-field";
import { Cell, Row } from "@material/react-layout-grid";
import { useEditGroupCustomerMutation } from "./queries/editGroupCustomer.hotchoc.generated";
import { CustomerFormData, ProjectRequestFormData } from "./types";
import { useGroupReviewContext } from "../GroupReview/groupReviewContext";
import { toCustomerValues } from "./utils";
import { CustomerStatus, ValidationResponse } from "~/gql/types";
import { Body1 } from "@material/react-typography";

type GroupEditCustProps = {
  initialFormValues: Partial<ProjectRequestFormData>;
  memberId: string;
  /**
   * When false, this is a single (non-grouped) request: tweak the copy and
   * refresh via onSaved instead of the group-review context. Defaults to true.
   */
  isGroup?: boolean;
  /**
   * Refresh callback used after an immediate save. The group-review screen
   * relies on its context refetch; the single-request Edit screen passes its
   * own reload here.
   */
  onSaved?: () => void;
};

type FormData = CustomerFormData & {
  notes: string | null;
};

/**
 * The dialog is a two-step wizard: step 1 chooses what to do, step 2 collects
 * the details for that choice.
 */
type WizardMode = "existing" | "new" | "edit";

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  button {
    width: 100%;
  }
`;

const customerFieldSet = (values: { isInternational?: boolean }) => (
  <Row>
    <Cell columns={6}>
      <TextFormField
        formField={"customerName"}
        label={"Customer Name"}
        type="string"
        helperText={""}
        required
      />
      <TextFormField
        formField={"customerAddress1"}
        label={"Customer Address"}
        type="string"
        helperText={""}
        required
      />
      <TextFormField
        formField={"customerAddress2"}
        label={"Address 2"}
        type="string"
        helperText={""}
      />
      <TextFormField
        formField={"customerPhone"}
        label={"Customer Phone"}
        type="string"
        helperText={""}
        required
      />
      <TextFormField
        formField={"customerFax"}
        label={"Customer Fax"}
        type="string"
        helperText={""}
      />
      <TextFormField
        formField={"customerAdminNotes"}
        label={"Customer Internal Notes"}
        type="string"
        helperText={""}
      />
    </Cell>
    <Cell columns={6}>
      <BinaryPickerFormField
        formField="isInternational"
        label="is International"
        helperText=""
      />
      <TextFormField
        formField={"customerCity"}
        label={"City"}
        type="string"
        helperText={""}
        required
      />
      {values.isInternational && (
        <TextFormField
          formField={"customerCountry"}
          label={"Country"}
          type="string"
          helperText={""}
        />
      )}
      {!values.isInternational && (
        <>
          <TextFormField
            formField={"customerState"}
            label={"State"}
            type="string"
            helperText={""}
          />
          <TextFormField
            formField={"customerZip"}
            label={"Zip"}
            type="string"
            helperText={""}
          />
        </>
      )}
      <TextFormField
        formField={"customerNotes"}
        label={"Customer Notes"}
        type="string"
        helperText={""}
      />
      <TextFormField
        formField={"notes"}
        label={"Admin Notes"}
        type="string"
        helperText={""}
        required
      />
      <TextFormField
        formField={"customerAttn"}
        label={"Attn"}
        type="string"
        helperText={""}
      />
    </Cell>
  </Row>
);

type WizardBodyProps = {
  isInProjectRequest: boolean;
  onCancel: () => void;
};

const WizardBody: React.FC<WizardBodyProps> = ({
  isInProjectRequest,
  onCancel,
}) => {
  const form = useForm();
  const [step, setStep] = useState<"choice" | "details">("choice");
  const [mode, setMode] = useState<WizardMode | null>(null);

  const chooseMode = (chosen: WizardMode) => {
    // Drive the existing toCustomerValues branch selection off the wizard mode.
    form.change("requestNewCustomer", chosen === "new");
    form.change("editInPlace", chosen === "edit");
    setMode(chosen);
    setStep("details");
  };

  const goBack = () => {
    form.change("requestNewCustomer", false);
    form.change("editInPlace", false);
    setStep("choice");
  };

  if (step === "choice") {
    return (
      <ChoiceList className="customer-wizard-choice">
        <Button
          type="button"
          outlined
          className="choose-existing"
          onClick={() => chooseMode("existing")}
        >
          Pick an existing customer
        </Button>
        <Button
          type="button"
          outlined
          className="choose-new"
          onClick={() => chooseMode("new")}
        >
          Request a new customer
        </Button>
        {isInProjectRequest && (
          <Button
            type="button"
            outlined
            className="choose-edit"
            onClick={() => chooseMode("edit")}
          >
            Edit the current customer
          </Button>
        )}
        <Button type="button" className="wizard-cancel" onClick={onCancel}>
          Cancel
        </Button>
      </ChoiceList>
    );
  }

  return (
    <>
      <ValueRenderer
        render={(values) =>
          mode === "existing" ? (
            <>
              <CustomerPicker
                formField="customer"
                label="Customer"
                helperText=""
                required
              />
              <TextFormField
                formField={"notes"}
                label={"Admin Notes"}
                type="string"
                helperText={""}
                required
              />
            </>
          ) : (
            customerFieldSet(values)
          )
        }
      />

      <ErrorRenderer
        render={(formError) => (
          <FormBottomRow
            errorMessage={formError}
            buttonText="Submit"
            onCancel={onCancel}
          >
            <Button type="button" className="wizard-back" onClick={goBack}>
              Back
            </Button>
          </FormBottomRow>
        )}
      />
    </>
  );
};

export const GroupCustomerForm: React.FC<GroupEditCustProps> = ({
  initialFormValues,
  memberId,
  isGroup = true,
  onSaved,
}) => {
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [doMutation] = useEditGroupCustomerMutation();
  const addAlert = useSnackBar();
  const { refetch } = useGroupReviewContext();

  const onSuccess = () => {
    addAlert({
      isSuccess: true,
      message: "Customer Edited",
      key: `${Math.random()}`,
    });
    setEditCustomerOpen(false);
    if (onSaved) {
      onSaved();
    } else {
      void refetch();
    }
  };

  const runMutation = async (values: FormData) => {
    const input = {
      notes: values.notes!,
      memberId: memberId,
      customer: toCustomerValues(values),
    };
    const res = await doMutation({ variables: { input } });

    return res?.data?.projectRequests?.editGroupCustomer as ValidationResponse;
  };
  const customer = initialFormValues.customer!;
  const customerName = customer.name;
  const customerNumber = customer.number ?? "requested";

  const isInProjectRequest = customer.status == CustomerStatus.InProjectRequest;

  // When the attached customer is InProjectRequest, the reviewer may edit its
  // fields in place; seed those fields so the edit form starts populated.
  const initialValues: FormData = {
    customerAddress2: isInProjectRequest ? customer.address2 : null,
    notes: null,
    customerFax: isInProjectRequest ? customer.fax : null,
    customerNotes: isInProjectRequest ? customer.notes : null,
    customerAdminNotes: isInProjectRequest ? customer.adminNotes ?? null : null,
    customerAttn: isInProjectRequest ? customer.attn ?? null : null,
    customer: customer.status == CustomerStatus.Rejected ? null : customer,
    customerName: isInProjectRequest ? customer.name : null,
    customerState: isInProjectRequest ? customer.state : null,
    customerZip: isInProjectRequest ? customer.zip : null,
    customerCountry: isInProjectRequest ? customer.country : null,
    customerAddress1: isInProjectRequest ? customer.address1 : null,
    customerPhone: isInProjectRequest ? customer.phone : null,
    customerCity: isInProjectRequest ? customer.city : null,
    isInternational: isInProjectRequest ? !!customer.country : false,
    requestNewCustomer: false,
    editInPlace: false,
  };

  return (
    <>
      <dt className="request-field-label">Customer</dt>
      <dd>
        {`${customerName} (${customerNumber})`}
        <Button
          className="group-edit-button open-edit-cust-button"
          outlined
          onClick={() => setEditCustomerOpen(true)}
        >
          Edit
        </Button>
      </dd>

      <Dialog
        open={editCustomerOpen}
        onClose={() => setEditCustomerOpen(false)}
        data-open={editCustomerOpen}
        className="edit-group-dialog"
      >
        <DialogTitle>
          {isGroup
            ? "Editing the customer will apply to the entire group"
            : "Edit the customer for this request"}
        </DialogTitle>
        <DialogContent>
          {window.config.featureFlags.projectFolders && (
            <Body1>
              Any requests in this group with a grouped folder will require a
              new folder to be selected.
            </Body1>
          )}
          {editCustomerOpen && (
            <MutationForm
              initialValues={initialValues}
              runMutation={runMutation}
              onSuccess={onSuccess}
            >
              <WizardBody
                isInProjectRequest={isInProjectRequest}
                onCancel={() => setEditCustomerOpen(false)}
              />
            </MutationForm>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
